<template>
  <div v-if="isLendflowUser && phoneNumber" class="flex items-center gap-2">
    <tooltip interactive>
      <template #default>
        <div
          class="action-icon call-action flex cursor-pointer items-center justify-center rounded-full"
          data-cy="quick-call-action"
          @click.stop="handleCall"
        >
          <icon-base
            :icon="IconTelephone"
            width="10"
            height="10"
            view-box="0 0 16 16"
            icon-color="currentColor"
          />
        </div>
      </template>
      <template #content>
        <telephone-tooltip-content class="flex items-center space-x-2">
          <span>{{ phoneNumber }}</span>
        </telephone-tooltip-content>
      </template>
    </tooltip>
    <div
      v-tooltip="t('COMMON.SMS')"
      class="action-icon sms-action flex cursor-pointer items-center justify-center rounded-full"
      data-cy="quick-sms-action"
      @click.stop="handleSms"
    >
      <icon-base
        :icon="IconMessage"
        width="10"
        height="10"
        view-box="0 0 16 16"
        icon-color="currentColor"
      />
    </div>
    <send-deal-communication-modal
      v-if="showSmsModal"
      :standalone="!currentDeal?.id"
      :deal-id="currentDeal?.id"
      :phone-number="phoneNumber"
      :type="CommunicationType.sms"
      @close-dialog="showSmsModal = false"
    />
    <teleport v-if="callDialog" to="#modals">
      <communication-log-call-dialog
        class="fixed z-50 shadow-lg"
        :value="callDialog"
        @close-dialog="callDialog = null"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { AnalyticsEvent } from "@/enums/analytics";
import { CallErrorReason, CommunicationType } from "@/enums/communicationLogs";
import { setActiveCallSession } from "@/helpers/communication";
import { DEFAULT_DIALER } from "@/helpers/constants";
import { PHONE_ACTION_MODAL_OPEN } from "@/helpers/constants/events";
import { useAuth } from "@/hooks/auth";
import { useEmitter } from "@/hooks/common";
import { useTwilio } from "@/hooks/communicationProviders";
import { useContacts } from "@/hooks/contacts";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { useAnalyticsStore } from "@/stores/analytics";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import TelephoneTooltipContent from "@/components/communication/TelephoneTooltipContent.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IApplication } from "@/models/applications";
import type { CallLog } from "@/models/communicationLogs";

interface Props {
  phoneNumber: string;
  deal?: IApplication | null;
  shouldSetContacts?: boolean;
}

const { phoneNumber, deal, shouldSetContacts = true } = defineProps<Props>();

const { t } = useI18n();
const { showMessage } = useNotification();
const { trackEvent } = useAnalyticsStore();
const { activeDeal } = useDealsBase();

const { isLendflowUser } = useAuth();
const { preferredDialer } = useTwilio();
const emitter = useEmitter();

const showSmsModal = ref(false);
const callDialog = ref<CallLog | null>(null);

const communicationToolOptions = useLocalStorageSetting(
  "communicationToolOptions",
  {
    x: 0,
    y: 0,
    showAsPopover: false,
  }
);

const currentDeal = computed(() => deal ?? activeDeal.value);

const { callClientStandalone, setTelephoneForApplication, callClient } =
  useContacts(currentDeal);

const handleCall = async (e?: MouseEvent) => {
  if (!phoneNumber || phoneNumber === "-") {
    showMessage(t("TWILIO.NO_PHONE_NUMBER"), "error");
    trackEvent(AnalyticsEvent.CallOutError, {
      reason: CallErrorReason.MissingPhone,
    });
    return;
  }

  if (!currentDeal.value?.id) {
    // Standalone call
    const result = await callClientStandalone({
      to: phoneNumber,
      provider: preferredDialer.value ?? DEFAULT_DIALER,
    });
    if (!result?.connectionLog?.id) {
      trackEvent(AnalyticsEvent.CallOutError, {
        reason: CallErrorReason.StandaloneCallMissingApiResponse,
      });
      return;
    }
    setActiveCallSession(result);
    callDialog.value = result.connectionLog;
    return;
  }

  // Set the phone number for the call
  setTelephoneForApplication(currentDeal.value.id, phoneNumber);

  const result = await callClient(e, shouldSetContacts);
  if (!result?.connectionLog?.id) {
    trackEvent(AnalyticsEvent.CallOutError, {
      reason: CallErrorReason.MissingApiResponse,
    });
    showMessage(t("TWILIO.CALL_OUT_ERROR"), "error");
    return;
  }

  setActiveCallSession(result);
  if (result.event) {
    communicationToolOptions.value.x = result.event.x;
    communicationToolOptions.value.y = result.event.y;
  }
  callDialog.value = result.connectionLog;
};

const handleSms = () => {
  if (!phoneNumber || phoneNumber === "-") {
    showMessage(t("ACTIVITY.CONTACTS.NO_PHONE_NUMBER"), "error");
    return;
  }

  showSmsModal.value = true;
};

watch(
  () => showSmsModal.value || !!callDialog.value,
  (hasOpenModal) => emitter.emit(PHONE_ACTION_MODAL_OPEN, hasOpenModal),
  { immediate: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.action-icon {
  @apply h-5 w-5 bg-light-grey text-label transition-colors duration-150;
}

.call-action:hover:not(.disabled) {
  @apply bg-green-50 text-success;
}

.sms-action:hover {
  @apply bg-primary-background-active text-primary;
}
</style>
